/*
  Once we test and mature this code a little more and add the functionality to pull plans from the API,
  we can evaluate what of this code should be made shared in the refresh-lib
*/
// START IMPORTS
// Core lib payments imports
import { costPerReport, savings } from '@ltvco/refresh-lib/payments';
// Core lib v1 imports
import { type SubscriptionPlan } from '@ltvco/refresh-lib/v1';
// END IMPORTS

// START INTERFACES_AND_TYPES
export interface CurrentPlan {
  title: string;
  amount: number;
  /** Renewal period in months */
  renewalPeriod: number;
  /** Report limit */
  limit: number;
  costPerReport?: number;
}
// END INTERFACES_AND_TYPES

// START CONSTANTS
export const customPlans: {
  [key: number]: [SubscriptionPlan];
} = {
  3113: [
    {
      amazon_id: 'none for this plan',
      amount: 7499,
      app_only: 'Web',
      apple_id: 'none for this plan',
      google_id: 'none for this plan',
      name: 'Premier Plan - Includes 500 Reports Per Month',
      title: 'Monthly Plan 500',
      subtitle: '500 Reports - $74.99 plus applicable sales tax',
      recurring: true,
      renewal_period: 1,
      renewal_period_type: 'months',
      report_tier: 'person_report',
      unique_key:
        '74_99_1_month_nofree_afill_limit_500_freepdf_peoplelooker_upsell',
      internal_name:
        'Subscription_$74.99_1Months_0FreeTrial_Recurring_Limit500_nofreeafill_freePDF_PeopleLooker_upsell',
      discount: null,
      public_price_description: '$74.99 plus applicable sales tax every month',
      default: false,
      monthly_report_limit: 500,
    },
  ],
  3045: [
    {
      amazon_id: 'none for this plan',
      amount: 18999,
      app_only: 'Web',
      apple_id: 'none for this plan',
      google_id: 'none for this plan',
      name: 'Premier Plan - Includes 2000 Reports Per Month',
      title: 'Monthly Plan 2000',
      subtitle: '2000 Reports - $189.99 plus applicable sales tax',
      recurring: true,
      renewal_period: 1,
      renewal_period_type: 'months',
      report_tier: 'person_report',
      unique_key:
        '189_99_1_month_nofree_afill_limit_2000_freepdf_peoplelooker_upsell',
      internal_name:
        'Subscription_$189.99_1Months_0FreeTrial_Recurring_Limit2000_nofreeafill_freePDF_PeopleLooker_upsell',
      discount: null,
      public_price_description: '$189.99 plus applicable sales tax every month',
      default: false,
      monthly_report_limit: 2000,
    },
  ],
  3046: [
    {
      amazon_id: 'none for this plan',
      amount: 23999,
      app_only: 'Web',
      apple_id: 'none for this plan',
      google_id: 'none for this plan',
      name: 'Premier Plan - Includes 3000 Reports Per Month',
      title: 'Monthly Plan 3000',
      subtitle: '3000 Reports - $239.99 plus applicable sales tax',
      recurring: true,
      renewal_period: 1,
      renewal_period_type: 'months',
      report_tier: 'person_report',
      unique_key:
        '239_99_1_month_nofree_afill_limit_3000_freepdf_peoplelooker_upsell',
      internal_name:
        'Subscription_$239.99_1Months_0FreeTrial_Recurring_Limit3000_nofreeafill_freePDF_PeopleLooker_upsell',
      discount: null,
      public_price_description: '$239.99 plus applicable sales tax every month',
      default: false,
      monthly_report_limit: 3000,
    },
  ],
  3047: [
    {
      amazon_id: 'none for this plan',
      amount: 31999,
      app_only: 'Web',
      apple_id: 'none for this plan',
      google_id: 'none for this plan',
      name: 'Premier Plan - Includes 5000 Reports Per Month',
      title: 'Monthly Plan 5000',
      subtitle: '5000 Reports - $319.99 plus applicable sales tax',
      recurring: true,
      renewal_period: 1,
      renewal_period_type: 'months',
      report_tier: 'person_report',
      unique_key:
        '319_99_1_month_nofree_afill_limit_5000_freepdf_peoplelooker_upsell',
      internal_name:
        'Subscription_$319.99_1Months_0FreeTrial_Recurring_Limit5000_nofreeafill_freePDF_PeopleLooker_upsell',
      discount: null,
      public_price_description: '$319.99 plus applicable sales tax every month',
      default: false,
      monthly_report_limit: 5000,
    },
  ],
};

export const planTitles: {
  [key: number]: { title: string; description: string };
} = {
  100: { title: 'Starter', description: 'Starting small and simple' },
  200: { title: 'Standard', description: 'Double your search capacity' },
  400: { title: 'Premium', description: 'Your premium reports package' },
  500: { title: 'Premier +', description: 'Perfect for fast-tracking growth' },
  1000: {
    title: 'Professional',
    description: 'Take your business to the next level',
  },
  2000: { title: 'Premier +', description: 'Perfect for fast-tracking growth' },
  3000: { title: 'Premier +', description: 'Perfect for fast-tracking growth' },
  5000: { title: 'Premier +', description: 'Perfect for fast-tracking growth' },
};

const fallbackTitles: { [key: number]: { title: string } } = {
  100: { title: 'Lite User' },
  200: { title: 'Lite User' },
  400: { title: 'Plus User' },
  2000: { title: 'Elite User' },
};

const mostPopularPlanLimit = 200;
// END CONSTANTS

// START HELPER_FUNCTIONS
export function getPlanTitleAndDescription(planLimit: number) {
  return (
    planTitles[planLimit] ||
    fallbackTitles[planLimit] || { title: 'Power User' }
  );
}

/** Returns the cost per report of the plan if it's not unlimited */
export function planCostPerReport(plan: {
  amount: number;
  renewalPeriod: number;
  limit: number;
}) {
  if (plan.limit === -1) return;

  return costPerReport(plan.amount, plan.renewalPeriod, plan.limit);
}

/** Returns the savings of the plan compared to the current plan */
export function planSavings(plan: SubscriptionPlan, currentPlan: CurrentPlan) {
  if (currentPlan.limit === -1 || plan.monthly_report_limit === -1) return;

  return savings(
    currentPlan.amount,
    currentPlan.renewalPeriod,
    currentPlan.limit,
    plan.amount / 100,
    plan.renewal_period,
    plan.monthly_report_limit
  );
}

/** Returns the plans that are better than the current plan */
export function getPlans(
  brandPlans: SubscriptionPlan[],
  currentPlan: CurrentPlan,
  customPlanId?: number
) {
  const selectedPlans = customPlanId ? customPlans[customPlanId] : brandPlans;

  return selectedPlans
    .filter((plan) => plan.monthly_report_limit > currentPlan.limit)
    .map((plan) => mapPlan(plan, currentPlan));
}

export function mapPlan(plan: SubscriptionPlan, currentPlan: CurrentPlan) {
  return {
    key: plan.unique_key,
    ...getPlanTitleAndDescription(plan.monthly_report_limit),
    amount: plan.amount / 100,
    limit: plan.monthly_report_limit,
    renewalPeriod: plan.renewal_period,
    renewalPeriodType: plan.renewal_period_type,
    costPerReport: planCostPerReport({
      amount: plan.amount / 100,
      renewalPeriod: plan.renewal_period,
      limit: plan.monthly_report_limit,
    }),
    savings: planSavings(plan, currentPlan),
    mostPopular: mostPopularPlanLimit === plan.monthly_report_limit,
  };
}

export function getBillingFrequency(renewalPeriod: number) {
  switch (renewalPeriod) {
    case 1:
      return 'monthly';
    case 3:
      return 'every 3 months';
    case 12:
      return 'annually';
    default:
      return 'N/A';
  }
}
// END HELPER_FUNCTIONS
